/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {NumberFormat} from '@dnb/eufemia/src';
function _createMdxContent(props) {
  const _components = Object.assign({
    h2: "h2",
    pre: "pre",
    code: "code",
    p: "p",
    ul: "ul",
    li: "li",
    h3: "h3",
    h4: "h4",
    strong: "strong",
    a: "a",
    blockquote: "blockquote"
  }, _provideComponents(), props.components);
  return _jsxs(_Fragment, {
    children: [_jsx(_components.h2, {
      children: "Import"
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-tsx",
        children: "import { NumberFormat } from '@dnb/eufemia'\n"
      })
    }), "\n", _jsx(_components.h2, {
      children: "Description"
    }), "\n", _jsx(_components.p, {
      children: "A ready-to-use DNB number formatter. Use it wherever you have to display a number, a currency value, phone number, etc."
    }), "\n", _jsx(_components.p, {
      children: "Good reasons for why we have this is to:"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "uniform the formation of numbers for all DNB applications."
      }), "\n", _jsx(_components.li, {
        children: "and make numbers accessible to screen readers."
      }), "\n"]
    }), "\n", _jsx(_components.h3, {
      children: "Supported formats"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: ["Numbers in general e.g. ", _jsx("code", {
          className: "dnb-code",
          children: _jsx(NumberFormat, {
            value: "12345678.90"
          })
        })]
      }), "\n", _jsxs(_components.li, {
        children: ["Currency e.g. ", _jsx("code", {
          className: "dnb-code",
          children: _jsx(NumberFormat, {
            currency: true,
            value: "12345678.90"
          })
        })]
      }), "\n", _jsxs(_components.li, {
        children: ["Percentage e.g. ", _jsx("code", {
          className: "dnb-code",
          children: _jsx(NumberFormat, {
            percent: true,
            value: "12.34"
          })
        })]
      }), "\n", _jsxs(_components.li, {
        children: ["Phone numbers e.g. ", _jsx("code", {
          className: "dnb-code",
          children: _jsx(NumberFormat, {
            phone: true,
            value: "004799999999"
          })
        })]
      }), "\n", _jsxs(_components.li, {
        children: ["Bank account number e.g. ", _jsx("code", {
          className: "dnb-code",
          children: _jsx(NumberFormat, {
            ban: true,
            value: "20001234567"
          })
        })]
      }), "\n", _jsxs(_components.li, {
        children: ["National identification number e.g. ", _jsx("code", {
          className: "dnb-code",
          children: _jsx(NumberFormat, {
            nin: true,
            value: "18089212345"
          })
        })]
      }), "\n", _jsxs(_components.li, {
        children: ["Organization number e.g. ", _jsx("code", {
          className: "dnb-code",
          children: _jsx(NumberFormat, {
            org: true,
            value: "123456789"
          })
        })]
      }), "\n", _jsxs(_components.li, {
        children: ["Compact (short) numbers e.g. ", _jsx("code", {
          className: "dnb-code",
          children: _jsx(NumberFormat, {
            compact: true,
            value: "12345678",
            decimals: 1
          })
        })]
      }), "\n", _jsxs(_components.li, {
        children: ["Compact (long) currency e.g. ", _jsx("code", {
          className: "dnb-code",
          children: _jsx(NumberFormat, {
            compact: "long",
            currency: true,
            currency_display: "name",
            value: "12345678",
            decimals: 1
          })
        })]
      }), "\n"]
    }), "\n", _jsx(_components.h3, {
      children: "Defaults"
    }), "\n", _jsxs(_components.p, {
      children: ["It uses the browser APIs ", _jsx(_components.code, {
        children: "number.toLocaleString"
      }), " or ", _jsx(_components.code, {
        children: "Intl.NumberFormat.format"
      }), " under the hood. As well as some custom formatter. The locale defaults to:"]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: ["Locale: ", _jsx(_components.code, {
          children: "nb-NO"
        })]
      }), "\n", _jsxs(_components.li, {
        children: ["Currency: ", _jsx(_components.code, {
          children: "NOK"
        })]
      }), "\n"]
    }), "\n", _jsx(_components.h4, {
      children: "Norwegian kroner"
    }), "\n", _jsxs(_components.p, {
      children: ["When the currency format is set to ", _jsx(_components.code, {
        children: "currency_display=\"name\""
      }), ", the currency will be displayed as \"kroner\" instead of \"Norwegian kroner\"."]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: ["Norwegian currency: ", _jsx("code", {
          className: "dnb-code",
          children: _jsx(NumberFormat, {
            currency: true,
            currency_display: "name",
            value: "1234.90"
          })
        })]
      }), "\n", _jsxs(_components.li, {
        children: ["Swedish currency: ", _jsx("code", {
          className: "dnb-code",
          children: _jsx(NumberFormat, {
            currency: "SEK",
            currency_display: "name",
            value: "1234.90"
          })
        })]
      }), "\n"]
    }), "\n", _jsx(_components.h4, {
      children: "Not available"
    }), "\n", _jsxs(_components.p, {
      children: ["When a number should be displayed, but for some reason is not available to the frontend application, the NumberFormat component will display a single ", _jsx(_components.strong, {
        children: "em dash"
      }), " (–) and a screen reader will get a text (Ikke tilgjengelig / Not available)."]
    }), "\n", _jsxs(_components.p, {
      children: ["Example: ", _jsx(NumberFormat, {
        value: "invalid",
        currency: true
      })]
    }), "\n", _jsx(_components.h2, {
      children: "Decimals"
    }), "\n", _jsxs(_components.p, {
      children: ["If the value has more decimal places than specified by the ", _jsx(_components.code, {
        children: "decimals={2}"
      }), " property, it will be rounded accordingly."]
    }), "\n", _jsxs(_components.p, {
      children: ["Here are the available options for the ", _jsx(_components.code, {
        children: "rounding"
      }), " property:"]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx(_components.code, {
          children: "omit"
        }), " truncate decimals without rounding."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.code, {
          children: "half-even"
        }), " will round to the nearest even number."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.code, {
          children: "half-up"
        }), " (default) will round up if the fractional part is 0.5 or greater, the number is rounded up. If the fractional part is less than 0.5, the number is rounded down."]
      }), "\n"]
    }), "\n", _jsx(_components.h2, {
      children: "Provider"
    }), "\n", _jsxs(_components.p, {
      children: ["You can send down the ", _jsx(_components.code, {
        children: "locale"
      }), " as an application-wide property (Context). More info about the ", _jsx(_components.a, {
        href: "/uilib/components/number-format/provider",
        children: "provider and locale usage"
      }), "."]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-jsx",
        children: "import Provider from '@dnb/eufemia/shared/Provider'\n\nrender(\n  <Provider locale=\"en-GB\" NumberFormat={{ currency_display: 'code' }}>\n    <MyApp>\n      text <NumberFormat>123</NumberFormat> table etc.\n    </MyApp>\n  </Provider>,\n)\n"
      })
    }), "\n", _jsx(_components.h2, {
      children: "NumberFormat Hook"
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Heads up:"
      }), " If you do so, keep in mind, you will have to ensure all the accessibility enhancements the component offers. For that you can use the ", _jsx(_components.code, {
        children: "aria"
      }), " field:"]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-jsx",
        children: "import Provider from '@dnb/eufemia/shared/Provider'\nimport useNumberFormat from '@dnb/eufemia/components/number-format/useNumberFormat'\n\nfunction Component() {\n  // By using returnAria you get an object\n  const { number, aria } = useNumberFormat(12345678.9, {\n    // Props are inherited from the Eufemia Provider and the NumberFormat object\n    returnAria: true,\n  })\n\n  return (\n    <span>\n      <span aria-hidden>{number}</span>\n      <span className=\"dnb-sr-only\">{aria}</span>\n    </span>\n  )\n}\n\nrender(\n  <Provider locale=\"en-GB\" NumberFormat={{ currency: 'EUR' }}>\n    <Component />\n  </Provider>,\n)\n"
      })
    }), "\n", _jsx(_components.h2, {
      children: "Formatting only (interceptor)"
    }), "\n", _jsxs(_components.p, {
      children: ["You can use the ", _jsx(_components.code, {
        children: "format"
      }), " method without using a React Component or React Hook."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "Heads up:"
      }), " If you do so, keep in mind, you will have to ensure all the accessibility enhancements the component offers. For that you can use the ", _jsx(_components.code, {
        children: "aria"
      }), " field:"]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-ts",
        children: "import { format } from '@dnb/eufemia/components/number-format/NumberUtils'\n\n// By using returnAria you get an object\nconst { number, aria } = format(12345678.9, {\n  locale: 'nb-NO', // not inherited\n  currency: true,\n  returnAria: true,\n})\n\n// Basic formatting\nconst number = format(1234)\n"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["The ", _jsx(_components.code, {
        children: "format"
      }), " method will accept the same ", _jsx(_components.a, {
        href: "/uilib/components/number-format/properties",
        children: "properties"
      }), " as the component."]
    }), "\n", _jsx(_components.h3, {
      children: "Interceptor helpers"
    }), "\n", _jsxs(_components.p, {
      children: ["Also, you may check out the related tests ", _jsx(_components.strong, {
        children: "NumberFormat > cleanNumber"
      }), " in the source code to find more examples."]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-ts",
        children: "import { cleanNumber } from '@dnb/eufemia/components/number-format/NumberUtils'\n\nconst string = cleanNumber('prefix -12 345,678 suffix') // returns -12345.678\nconst string = cleanNumber('prefix -12.345,678 suffix') // returns -12345.678\n"
      })
    }), "\n", _jsx(_components.h3, {
      children: "Element and style"
    }), "\n", _jsxs(_components.p, {
      children: ["The number component is style-independent, so it has no visual styles. By default, a ", _jsx(_components.code, {
        children: "<span>"
      }), " is used (with ", _jsx(_components.a, {
        href: "https://developer.mozilla.org/en-US/docs/Web/CSS/@counter-style/speak-as",
        children: "speak-as: numbers"
      }), ", even the support is very low). But you can easily change the element type by providing something else to ", _jsx(_components.code, {
        children: "element=\"div\""
      }), " property."]
    }), "\n", _jsx(_components.h2, {
      children: "Accessibility"
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "NVDA"
      }), " has also ", _jsx(_components.a, {
        href: "https://github.com/nvaccess/nvda/issues/8874",
        children: "issues"
      }), " on reconciling the ", _jsx(_components.code, {
        children: "lang"
      }), " attribute, this makes it hard to have a solid and good solution for reading numbers. VoiceOver on desktop makes a perfect job there."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "VoiceOver"
      }), " on mobile devices (iOS) only supports numbers read out properly to a maximum of ", _jsx(_components.code, {
        children: "99,999.00"
      }), ". On amounts above this value, VO reads numbers digit by digit."]
    }), "\n", _jsxs(_components.p, {
      children: ["To enhance the ", _jsx(_components.strong, {
        children: "Copy & Paste"
      }), " experience of copying numbers into other applications (Excel), you may use the ", _jsx(_components.code, {
        children: "clean_copy_value"
      }), " property – it will then provide a second number, without thousand separators and to have a comma/dot (depending on the locale) as the decimal separator. This number is not visible, but will be used when selecting & copying the whole number on the first click to the system clipboard."]
    }), "\n", _jsxs(_components.p, {
      children: ["You can enable this feature on all your NumberFormat components by using the ", _jsx(_components.code, {
        children: "Provider"
      }), ":"]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-jsx",
        children: "import { Provider } from '@dnb/eufemia/shared'\n\nrender(\n  <Provider value={{ NumberFormat: { clean_copy_value: true } }}>\n    <YourApp />\n  </Provider>,\n)\n"
      })
    }), "\n", _jsx(_components.h3, {
      children: "More details"
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsxs(_components.p, {
        children: ["Screen readers require numbers to be formatted properly in order to be read as numbers. The ", _jsx(_components.strong, {
          children: "NumberFormat"
        }), " component will help to achieve this requirement."]
      }), "\n"]
    }), "\n", _jsxs(_components.p, {
      children: ["So, numbers are formatted differently for screen readers than the visual number. And numbers also get assigned a ", _jsx(_components.code, {
        children: "lang"
      }), " attribute, so the screen reader knows what language (locale) should be used on the particular number, even if the text around does not correspond to the same language."]
    }), "\n", _jsx(_components.h3, {
      children: "Sources"
    }), "\n", _jsxs(_components.p, {
      children: ["Eufemia is basing their number formats on both the ", _jsx(_components.a, {
        href: "https://lovdata.no/forskrift/2004-02-16-426/%C2%A716",
        children: "Norwegian authority"
      }), " and ", _jsx(_components.a, {
        href: "https://www.sprakradet.no/sprakhjelp/Skriveregler/Dato",
        children: "Språkradet"
      }), ", and currency is based on ", _jsx(_components.a, {
        href: "https://www.sprakradet.no/svardatabase/sporsmal-og-svar/kronebelop-rekkjefolgje-komma-og-strek/",
        children: "guidelines"
      }), " from Språkrådet. Wikipedia has more info on world wide ", _jsx(_components.a, {
        href: "https://en.wikipedia.org/wiki/Decimal_separator",
        children: "decimal separator"
      }), " usage."]
    }), "\n", _jsxs(_components.p, {
      children: ["For international number formatting, we use the ", _jsx(_components.a, {
        href: "https://ec.europa.eu/info/sites/default/files/styleguide_english_dgt_en.pdf",
        children: "EU Style Guide"
      }), "."]
    }), "\n", _jsx(_components.h2, {
      children: "Node.js and SSR usage"
    }), "\n", _jsxs(_components.p, {
      children: ["If you run the component or ", _jsx(_components.code, {
        children: "format"
      }), " function in ", _jsx(_components.a, {
        href: "https://nodejs.org",
        children: "Node.js"
      }), " you have to include ", _jsx(_components.a, {
        href: "https://nodejs.org/api/intl.html",
        children: "ICU"
      }), " data in order to display other locales than en-GB. You can do this by:"]
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: ["installing ", _jsx(_components.code, {
          children: "npm i full-icu"
        })]
      }), "\n", _jsxs(_components.li, {
        children: ["and call node (or jest) with an environment variable pointing to the package: ", _jsx(_components.code, {
          children: "NODE_ICU_DATA=./node_modules/full-icu node ..."
        })]
      }), "\n", _jsxs(_components.li, {
        children: ["after a Node.js version upgrade you may have to run ", _jsx(_components.code, {
          children: "npm rebuild"
        })]
      }), "\n"]
    }), "\n", _jsx(_components.h2, {
      children: "Known issues"
    }), "\n", _jsx(_components.p, {
      children: "Edge Browser on Windows 10 is converting numbers automatically to followable links. This makes the experience on NVDA bad, as it reads also the new, unformatted link number."
    }), "\n", _jsxs(_components.p, {
      children: ["You can ", _jsx(_components.a, {
        href: "https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/x-ms-format-detection",
        children: "disable this behavior"
      }), ":"]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-html",
        children: "<html x-ms-format-detection=\"none\">\n  ...\n</html>\n"
      })
    }), "\n", _jsx(_components.h3, {
      children: "Lighthouse and axe-core"
    }), "\n", _jsxs(_components.p, {
      children: ["In order to enhance the UX while using a screen reader, the NumberFormat component is using a role called ", _jsx(_components.code, {
        children: "role=\"text\""
      }), ". This allows the screen reader to read particular numbers within the context, without interrupting the flow while reading paragraphs."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.a, {
        href: "https://developers.google.com/web/tools/lighthouse",
        children: "Lighthouse"
      }), " is using ", _jsx(_components.a, {
        href: "https://developers.google.com/web/tools/lighthouse",
        children: "axe-core"
      }), " under the hood. Older versions of ", _jsx(_components.code, {
        children: "axe-core"
      }), " is allowing us to use ", _jsx(_components.code, {
        children: "role=\"text\""
      }), " only within no focusable descendants. But because the NumberFormat component also includes a ", _jsx(_components.strong, {
        children: "Copy & Paste"
      }), " feature, it uses ", _jsx(_components.code, {
        children: "tabindex=\"-1\""
      }), " which allows JavaScript to focus the descendant DOM Element."]
    })]
  });
}
function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = Object.assign({}, _provideComponents(), props.components);
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, props)
  })) : _createMdxContent(props);
}
export default MDXContent;
